import React, { useRef, useState } from "react";
import Header from "./Header";
import { checkDataValidation } from "../utils/validate";
import {
	createUserWithEmailAndPassword,
	signInWithEmailAndPassword,
	updateProfile,
} from "firebase/auth";
import { auth } from "../utils/firebase";
import { useDispatch } from "react-redux";
import { addUser } from "../utils/userSlice";
import { PROFILE_IMG, HOME_BG } from "../utils/constants";
import { CgDanger } from "react-icons/cg";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";

const Login = () => {
	const [isSignIn, setIsSignIn] = useState(true);
	const [nameError, setNameError] = useState(null);
	const [emailError, setEmailError] = useState(null);
	const [passwordError, setPasswordError] = useState(null);
	const [authError, setAuthError] = useState(null);
	const [showPassword, setShowPassword] = useState(false);

	const dispatch = useDispatch();

	const userName = useRef(null);
	const userEmail = useRef(null);
	const userPassword = useRef(null);

	const handleSignInClick = () => {
		setEmailError("");
		setPasswordError("");

		setIsSignIn(!isSignIn);
	};

	const handleFormSubmission = (e) => {
		const errors = checkDataValidation(
			!isSignIn ? userName.current.value : null,
			userEmail.current.value,
			userPassword.current.value,
			isSignIn
		);

		setEmailError(errors[0]);
		setPasswordError(errors[1]);
		setNameError(errors[2]);

		if (null !== errors[0] || null !== errors[1] || null !== errors[2]) return;

		if (!isSignIn) {
			// Sign Up Logic
			createUserWithEmailAndPassword(
				auth,
				userEmail.current.value,
				userPassword.current.value
			)
				.then((userCredential) => {
					// Signed up
					const user = userCredential.user;

					updateProfile(user, {
						displayName: userName.current.value,
						photoURL: PROFILE_IMG,
					})
						.then(() => {
							// Profile updated!
							const { uid, email, displayName, photoURL } = auth.currentUser;
							dispatch(
								addUser({
									uid: uid,
									email: email,
									displayName: displayName,
									photoURL: photoURL,
								})
							);
						})
						.catch((error) => {
							// An error occurred
							setAuthError(error.message);
						});
				})
				.catch((error) => {
					const errorCode = error.code;
					const errorMessage = error.message;

					if ("auth/email-already-in-use" === errorCode) {
						setAuthError(
							"The email is already in use. Please use another email"
						);
					} else {
						setAuthError(errorCode + "-" + errorMessage);
					}
				});
		} else {
			// Sign In Logic
			signInWithEmailAndPassword(
				auth,
				userEmail.current.value,
				userPassword.current.value
			)
				.then(() => {
					// Signed in
				})
				.catch((error) => {
					const errorCode = error.code;
					const errorMessage = error.message;

					if ("auth/invalid-credential" === errorCode) {
						setAuthError("Invalid Login Credentials. Please try again");
					} else {
						setAuthError(errorCode + "-" + errorMessage);
					}
				});
		}
	};

	return (
		<div
			className="login-page relative w-full h-full flex flex-col bg-repeat overflow-auto bg-cover"
			style={{ backgroundImage: `url(${HOME_BG})` }}
		>
			<Header />
			<div className="flex justify-center items-center grow">
				<form
					className="bg-black/70 flex flex-col text-white px-6 py-8 lg:px-16 max-w-[300px] lg:max-w-[450px] w-full relative z-0 rounded-[4px]"
					onSubmit={(e) => e.preventDefault()}
				>
					<h2 className="text-2xl lg:text-5xl pb-8">
						{isSignIn ? "Sign In" : "Sign Up"}
					</h2>
					{!isSignIn && passwordError && (
						<div className="invalid-password-error bg-[#D89D31] text-black rounded-[4px] px-8 py-4">
							<p className="font-bold">Invalid Password</p>
							<p className="font-medium">
								Your password must contain atleast 8 characters and:
							</p>
							<ul className="list-disc">
								<li>Contain at least one digit</li>
								<li>Contain at least one lowercase letter</li>
								<li>Contain at least one uppercase letter</li>
							</ul>
						</div>
					)}
					<div className="flex flex-col gap-4">
						{!isSignIn && (
							<>
								<label htmlFor="full-name"></label>
								<input
									id="full-name"
									type="text"
									placeholder="Full Name"
									className="bg-black text-white pl-2 lg:pl-4 py-2 lg:py-4 border border-gray"
									ref={userName}
								/>
							</>
						)}
						{!isSignIn && nameError && (
							<span className="name-error form-error font-medium flex items-center gap-2">
								<CgDanger className="w-[20px] h-[20px]" />
								{nameError}
							</span>
						)}
						<label htmlFor="email-id" className="sr-only">
							Email
						</label>
						<input
							id="email-id"
							type="text"
							placeholder="Email"
							className="bg-black text-white pl-2 lg:pl-4 py-2 lg:py-4 border border-gray"
							ref={userEmail}
						/>
						{emailError && (
							<span className="email-error form-error relative font-medium flex items-center gap-2">
								<CgDanger className="w-[20px] h-[20px]" />
								{emailError}
							</span>
						)}
						<label htmlFor="password" className="sr-only">
							Password
						</label>
						<div className="relative">
							<input
								id="password"
								type={showPassword ? "text" : "password"}
								placeholder="Password"
								className="bg-black text-white pl-2 lg:pl-4 py-2 lg:py-4 border border-gray w-full pr-10"
								ref={userPassword}
							/>
							<button
								type="button"
								className="absolute inset-y-0 right-2 flex items-center text-white"
								onClick={() => setShowPassword(!showPassword)}
							>
								{showPassword ? (
									<AiOutlineEye size={20} />
								) : (
									<AiOutlineEyeInvisible size={20} />
								)}
							</button>
						</div>
						{passwordError && (
							<span
								className="password-error form-error font-medium flex
								items-center
								gap-2"
							>
								<CgDanger className="w-[20px] h-[20px]" />
								Invalid Password
							</span>
						)}
						{authError && (
							<span className="auth-error form-error font-medium flex items-center gap-2">
								<CgDanger className="w-[30px] h-[30px]" />
								{authError}
							</span>
						)}

						<button
							className="bg-red-600 py-4 hover:bg-red-700"
							onClick={handleFormSubmission}
						>
							{isSignIn ? "Sign In" : "Sign Up"}
						</button>
						{isSignIn ? (
							<p className="text-white/70">
								New to Watchflix?&nbsp;
								<span
									className="cursor-pointer font-bold text-white"
									onClick={(e) => handleSignInClick(e)}
								>
									Sign up now
								</span>
							</p>
						) : (
							<p className="text-white/70">
								Already Have an account?&nbsp;
								<span
									className="cursor-pointer font-bold text-white"
									onClick={(e) => handleSignInClick(e)}
								>
									Sign in now
								</span>
							</p>
						)}
					</div>
				</form>
			</div>
		</div>
	);
};

export default Login;
